import {
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ApiResponse } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';
import { isNotFoundError } from 'utils/api-utils/errors';

const ESTIMATED_SAVINGS_QUERY_KEY = 'estimated-savings';

type AvailableSavingsResponse = ApiResponse<
  typeof apiClient.costReport.getEstimatedSavings
>;

const getEstimatedSavingsQueryKey = (clusterId?: string) =>
  [ESTIMATED_SAVINGS_QUERY_KEY, clusterId] as const;

const getAvailableSavingsQueryOptions = (
  clusterId: string | undefined,
  isEnabled: boolean,
  client: QueryClient
) =>
  queryOptions({
    queryKey: getEstimatedSavingsQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getEstimatedSavings(
        { clusterId: clusterId! },
        { timeout: 60_000 }
      );
      return data;
    },
    enabled: !!clusterId && isEnabled,
    retry: (failureCount, error) => {
      if (isNotFoundError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    refetchInterval: ({ queryKey }) => {
      const data = client.getQueryData<AvailableSavingsResponse>(queryKey);
      if (!data) {
        return 5000;
      }
      return 0;
    },
  });

export const useAvailableSavingsQuery = (
  clusterId?: string,
  isEnabled = true
) => {
  const client = useQueryClient();
  const { isLoading, data, error, refetch, isFetched } = useQuery(
    getAvailableSavingsQueryOptions(clusterId, isEnabled, client)
  );

  return {
    isLoading,
    data: data || undefined,
    error,
    hasLoaded: isFetched,
    refetch,
  };
};
